import type { OrusSession } from '@orus.eu/backend'
import { isSuccess } from '@orus.eu/result'
import { getMissingPermissions, type AnyOfPermission, type Permission } from '@orus.eu/right-access-management'

import { createLoginRedirect } from './build-login-href.ts'
import { resetLoginBounce } from './detect-login-redirect-loop.ts'
import { readSessionForGuard } from './read-session-for-guard.ts'
import type { SessionGuardOptions } from './session-access.ts'

/**
 * Page-level access guard, called inside a route `beforeLoad`. Reads the session out of the router context,
 * and throws a redirect to the login page when the user is anonymous or lacks a required permission.
 *
 * Note: logged-in-but-forbidden currently redirects to login. A 403-in-place via errorComponent is a deliberate
 * follow-up (cf. routing-frontend.md, Décision 8).
 */
export async function requirePermissions(
  { context, location, preload }: SessionGuardOptions,
  requiredPermissions: (Permission | AnyOfPermission)[],
): Promise<void> {
  const hasRequiredPermissions = (session: OrusSession): boolean =>
    session.user !== null && getMissingPermissions(session.allPermissions, requiredPermissions).length === 0

  const sessionResult = await readSessionForGuard({ context, preload }, hasRequiredPermissions)

  // Session unavailable (client outdated): handled elsewhere by SessionManager — do not block the route.
  if (!isSuccess(sessionResult)) return

  if (!hasRequiredPermissions(sessionResult.output)) {
    throw createLoginRedirect(location.href, preload)
  }

  // Reached an authorized page: any in-flight bounce episode ended without looping
  if (!preload) {
    resetLoginBounce()
  }
}
